import React, {
  useEffect,
  useContext,
  createContext,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

export const activityContext = createContext();

// Wrap the hook with a provider
export const ProvideActivity = ({ children }) => {
  const inc = useProvideActivity();
  // We are providing the object returned by useProvideActivity as the value of the activityContext
  return <activityContext.Provider value={inc}>{children}</activityContext.Provider>;
};

ProvideActivity.propTypes = {
  children: PropTypes.element,
};

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useActivity = () => {
  // The context object will contain anything returned by useProvideActivity
  const context = useContext(activityContext);
  if (context === undefined) {
    throw new Error('useActivity must be used within a ProvideActivity');
  }
  return context;
};

const initialState = {
  activity: null,
  id: null,
  version: null,
  stale: true,
  loading: false,
  modifying: false,
  error: null,
};

export const ACTIONS = Object.freeze({
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  FETCH_ERROR: 'FETCH_ERROR',
  SET_MODIFYING: 'SET_MODIFYING',
  SET_STALE: 'SET_STALE',
  SET_VERSION: 'SET_VERSION',
  SET_ID: 'SET_ID',
  RESET: 'RESET',
});


const activityReducer = (nextState, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return initialState;
    case ACTIONS.FETCH_INIT:
      return {
        ...nextState,
        loading: true,
        error: null,
      };
    case ACTIONS.FETCH_SUCCESS:
      if (action.payload.deleted) {
        return {
          ...nextState,
          loading: false,
          error: 'Activity has been deleted',
          stale: false,
        };
      }

      return {
        ...nextState,
        loading: false,
        activity: action.payload,
        stale: false,
      };
    case ACTIONS.FETCH_ERROR:
      return {
        ...nextState,
        loading: false,
        activity: null,
        stale: false,
        error: 'Activity not found',
      };
    case ACTIONS.FETCH_FAILURE:
      return {
        ...nextState,
        loading: false,
        error: action.payload,
      };
    case ACTIONS.SET_ID: {
      let stale = nextState.id === action.payload ? false : true;
      return {
        ...nextState,
        stale,
        id: action.payload,
      };
    }
    case ACTIONS.SET_VERSION:
      return {
        ...nextState,
        version: action.payload,
      };
    case ACTIONS.SET_MODIFYING:
      return {
        ...nextState,
        modifying: action.payload,
      };
    case ACTIONS.SET_STALE:
      return {
        ...nextState,
        stale: action.payload,
      };
    default:
      throw new Error(`activityReducer this action is not supported: ${action.type}`);
  }
};

const useProvideActivity = () => {
  const controller = useRef(null);
  //activity state as a reducer
  const [activityState, dispatch] = useReducer(activityReducer, initialState);

  useEffect(() => {
    const {id, version, stale} = activityState;
    if (stale && id) {
      dispatch({type: ACTIONS.FETCH_INIT})
      let mounted = true;
      controller.current = new AbortController();
      const getActivity = () => {
        axios.get(`/api/activity/get?id=${id}${version === null ? '' : `&version=${version}`}`, { signal: controller.current.signal }).then(res => {
          if (mounted) {
            dispatch({type: ACTIONS.FETCH_SUCCESS, payload: res.data});
          }
        }
        ).catch(error => {
          if (error.name !== 'CanceledError') {
            dispatch({type: ACTIONS.FETCH_FAILURE, payload: error});
          }
        });
      }

      getActivity();

      // Cleanup on unmount
      return () => {
        controller.current.abort();
        mounted = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityState.stale, activityState.id]);

  return {
    unmount: () => {
      dispatch({type: ACTIONS.RESET});
      if (activityState.loading) {
        controller.current.abort();
      }
    },
    dispatch,
    activityState,
  }
};
